/**
 * Splits the cart for the products-page cart panel (TakeawayCart.js).
 *
 * Rows already sent to the kitchen (`is_previously_added`) collapse into one compact line each
 * (name, quantity, price - no customisations); rows not sent yet keep their full detail.
 * Pure functions, no React.
 */

const isPrevious = (row) => !!row && !!row.is_previously_added;

const money = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v : 0;
};

const qtyOf = (row) => {
  const q = Number(row?.item_quantity);
  return Number.isFinite(q) && q > 0 ? q : 1;
};

/** "Margherita - L": the name the cart row shows today, minus the dangling dash when there is no size. */
export const lineName = (row) => {
  const name = row?.item_name || '';
  const size = row?.selected_size ? String(row.selected_size).trim() : '';
  return size ? `${name} - ${size}` : name;
};

const toLine = (kind, row) => ({
  kind, // 'item' | 'deal' | 'half'
  key: `${kind}-${row.cart_id ?? row.id ?? row.cid ?? row.item_id}`,
  cartId: row.cart_id,
  name: kind === 'item' ? lineName(row) : row.item_name || '',
  qty: qtyOf(row),
  // a deal's own total_price leaves out its upgraded pieces; totalwithdeals is the whole line
  price: kind === 'deal' ? money(row.totalwithdeals ?? row.total_price) : money(row.total_price),
  row,
});

/**
 * @returns {{
 *   newDeals: object[], newHalfAndHalf: object[], newItems: object[],
 *   previousLines: {kind, key, cartId, name, qty, price, row}[],
 *   hasPrevious: boolean, hasNew: boolean
 * }}
 * newItems is most-recent-first (the order the cart has always listed them in); previousLines keep
 * cart order, deals and half-and-half first as before.
 */
export const splitCart = (cartItems, dealsItem, halfAndHalfItems) => {
  const items = Array.isArray(cartItems) ? cartItems : [];
  const deals = Array.isArray(dealsItem) ? dealsItem : [];
  const halves = Array.isArray(halfAndHalfItems) ? halfAndHalfItems : [];

  const newDeals = deals.filter((d) => !isPrevious(d));
  const newHalfAndHalf = halves.filter((h) => !isPrevious(h));
  const newItems = items.filter((i) => !isPrevious(i)).reverse();

  const previousLines = [
    ...deals.filter(isPrevious).map((d) => toLine('deal', d)),
    ...halves.filter(isPrevious).map((h) => toLine('half', h)),
    ...items.filter(isPrevious).map((i) => toLine('item', i)),
  ];

  return {
    newDeals,
    newHalfAndHalf,
    newItems,
    previousLines,
    hasPrevious: previousLines.length > 0,
    hasNew: newDeals.length + newHalfAndHalf.length + newItems.length > 0,
  };
};

/** "Previously added items (3)" */
export const previousGroupLabel = (count) => `Previously added items (${count})`;
